import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { OptionRenderConfig } from 'libs/survey-elements/multiple-choice/core/src/lib/multiple-choice-core';

type ImageScope = 'global' | 'element';
type ImageSource = 'upload' | 'html';
export interface OptionImageDialogData {
  optionId: string;
  optionDisplay: string;
  elementId: number | null;
  questionId: number | null;
  surveyId?: string | number | null;
  existingGlobal?: OptionRenderConfig | null;
  existingElement?: OptionRenderConfig | null;
}

export interface OptionImageSettings {
  scope: ImageScope;
  source: ImageSource;
  file?: File;
  html?: string;
  existingImageUrl?: string;
  imageRemoved?: boolean;
  showOptionText?: boolean;
}

@Component({
  selector: 'lib-add-list-option-image-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './add-list-option-image-dialog.html',
  styleUrl: './add-list-option-image-dialog.css',
})
export class AddListOptionImageDialog implements OnDestroy {
  readonly data = inject<OptionImageDialogData>(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<AddListOptionImageDialog>);

  // Global is the default while implementing global rendering.
  scope: ImageScope = 'global';
  source: ImageSource = 'upload';
  imageRemoved = false;
  showOptionText = true;
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  customHtml = '';

  constructor() {
    this.loadExistingSettings();
  }

  private getCurrentConfig(): OptionRenderConfig | null {
    return this.scope === 'global'
      ? (this.data.existingGlobal ?? null)
      : (this.data.existingElement ?? null);
  }

  private loadExistingSettings(): void {
    this.clearPreview();
    this.selectedFile = null;
    this.customHtml = '';
    this.imageRemoved = false;

    const config = this.getCurrentConfig();
    this.showOptionText = config?.show_option_text ?? true;

    if (!config) {
      this.source = 'upload';
      return;
    }

    if (config.render_type === 'html') {
      this.source = 'html';
      this.customHtml = config.custom_html ?? '';
      return;
    }

    this.source = 'upload';
    this.previewUrl = config.image_url ?? null;
  }

  onScopeChange(scope: ImageScope): void {
    // Only global settings are supported.
    this.scope = 'global';
    this.loadExistingSettings();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      input.value = '';
      return;
    }

    this.clearPreview();
    this.selectedFile = file;
    this.previewUrl = URL.createObjectURL(file);
    this.imageRemoved = false;
  }

  clearImage(fileInput?: HTMLInputElement): void {
    this.clearPreview();
    this.selectedFile = null;
    this.imageRemoved = true;

    if (fileInput) {
      fileInput.value = '';
    }
  }

  save(): void {
    if (this.scope === 'element' && this.data.questionId == null) {
      console.error('Survey element ID is missing.');
      return;
    }

    if (this.source === 'upload') {
      if (this.imageRemoved && !this.selectedFile && !this.previewUrl) {
        this.dialogRef.close({
          scope: this.scope,
          source: 'upload',
          imageRemoved: true,
          showOptionText: this.showOptionText,
        } satisfies OptionImageSettings);

        return;
      }

      if (!this.selectedFile && !this.previewUrl) {
        console.error('Please select an image before saving.');
        return;
      }

      this.dialogRef.close({
        scope: this.scope,
        source: 'upload',
        ...(this.selectedFile
          ? { file: this.selectedFile }
          : { existingImageUrl: this.previewUrl! }),
        showOptionText: this.showOptionText,
      } satisfies OptionImageSettings);

      return;
    }

    const html = this.customHtml.trim();

    if (!html) {
      console.error('Please enter custom HTML before saving.');
      return;
    }

    this.dialogRef.close({
      scope: this.scope,
      source: 'html',
      html,
      showOptionText: this.showOptionText,
    } satisfies OptionImageSettings);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private clearPreview(): void {
    if (this.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }

    this.previewUrl = null;
  }

  ngOnDestroy(): void {
    this.clearPreview();
  }
}
